import React, { useEffect, useState } from 'react';
import { Check, RefreshCw, RotateCcw, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../api/client';

// Impostazioni → FONTI ESTERNE (solo ROOT, 28/10/2026, titolare: "una sezione dedicata ai posti in cui punti
// all'esterno, metti che cambiano"). Ogni indirizzo da cui il gestionale legge dati (FIPAV, Club Series AIBVC)
// o che mostra come link: si cambia, si prova senza salvare, si torna al predefinito. Ogni cambio va nel Changelog.
const quando = (d) => d ? new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;

function Fonte({ f, onSalvata }) {
  const [url, setUrl] = useState(f.url);
  const [prova, setProva] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setUrl(f.url); }, [f.url]);
  const cambiato = url.trim() !== f.url;

  const fai = async (fn) => { setBusy(true); setErr(''); try { await fn(); } catch (e) { setErr(e.response?.data?.error || e.message); } finally { setBusy(false); } };
  const provaUrl = () => fai(async () => { setProva(null); setProva((await api.post(`/fonti/${f.chiave}/prova`, { url: url.trim() })).data); });
  const salva = (valore) => fai(async () => { onSalvata((await api.put(`/fonti/${f.chiave}`, { url: valore })).data); setProva(null); });

  return (
    <div style={st.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, flex: '1 1 300px' }}>
          <div style={{ fontWeight: 800 }}>{f.nome}</div>
          <div style={st.dim}>{f.uso}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ ...st.tag, ...(f.tipo === 'LETTO' ? st.tagBlu : st.tagGrigio) }}>{f.tipo === 'LETTO' ? 'DATI LETTI DA QUI' : 'LINK NELLE PAGINE'}</span>
          {f.personalizzato && <span style={{ ...st.tag, ...st.tagArancio }}>CAMBIATO</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        <input style={st.inp} value={url} onChange={e => { setUrl(e.target.value); setProva(null); }} spellCheck={false} />
        <button style={st.sec} disabled={busy} onClick={provaUrl} title="Controlla che risponda, senza salvare">{busy ? <RefreshCw size={13} /> : <Check size={13} />} Prova</button>
        {cambiato && <button style={st.pri} disabled={busy} onClick={() => salva(url.trim())}>Salva</button>}
        {f.personalizzato && !cambiato && <button style={st.sec} disabled={busy} onClick={() => salva('')} title={f.predefinito}><RotateCcw size={13} /> Predefinito</button>}
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ ...st.sec, textDecoration: 'none' }} title="Apri in una nuova scheda"><ExternalLink size={13} /></a>
      </div>
      {prova && <div style={prova.ok ? st.ok : st.ko}>{prova.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {prova.messaggio}{cambiato && prova.ok ? ' — premi Salva per usarlo' : ''}</div>}
      {err && <div style={st.ko}><AlertTriangle size={14} /> {err}</div>}
      <div style={{ ...st.dim, marginTop: 8 }}>
        {f.tipo === 'LETTO' && (f.ultimo_errore
          ? <span style={{ color: '#B42318' }}>Ultima lettura non riuscita {quando(f.ultimo_errore_at)}: {f.ultimo_errore}. </span>
          : f.ultimo_ok_at ? <span style={{ color: '#15803D' }}>Ultima lettura riuscita {quando(f.ultimo_ok_at)}. </span> : 'Non ancora letto con questo indirizzo. ')}
        {f.personalizzato ? `Cambiato da ${f.modificato_da || '?'} il ${quando(f.modificato_at)}. Predefinito: ${f.predefinito}` : 'Indirizzo predefinito.'}
      </div>
    </div>
  );
}

export default function FontiEsterne() {
  const [fonti, setFonti] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.get('/fonti').then(r => setFonti(r.data)).catch(e => setErr(e.response?.data?.error || e.message)); }, []);
  if (err) return <div style={st.ko}>{err}</div>;
  if (!fonti) return <div style={st.dim}>Caricamento…</div>;
  return (
    <div>
      <div style={{ ...st.dim, marginBottom: 14, maxWidth: 820, lineHeight: 1.5 }}>
        Qui ci sono tutti gli indirizzi esterni da cui il gestionale prende i dati (FIPAV, Club Series AIBVC) o che mostra come link.
        Se un sito cambia indirizzo, incolla quello nuovo, premi <b>Prova</b> per vedere se risponde con i dati giusti e poi <b>Salva</b>.
        Con <b>Predefinito</b> torni a quello di partenza. Ogni cambio resta nel Changelog. Solo https e siti internet pubblici.
        La pagina pubblica del calendario FIPAV (portal.b-cheers.it/fipav) legge invece da un indirizzo scritto nella configurazione del server: se cambia, va sistemato lì.
      </div>
      {['FIPAV', 'AIBVC'].map(area => (
        <div key={area} style={{ marginBottom: 18 }}>
          <div style={st.sez}>{area === 'AIBVC' ? 'AIBVC · CLUB SERIES' : 'FIPAV'}</div>
          {fonti.filter(f => f.area === area).map(f => <Fonte key={f.chiave} f={f} onSalvata={setFonti} />)}
        </div>
      ))}
    </div>
  );
}

const st = {
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 10 },
  dim: { fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' },
  sez: { fontSize: 'var(--font-size-xs)', fontWeight: 800, letterSpacing: '2px', color: 'var(--text-muted)', margin: '0 0 8px' },
  inp: { flex: '1 1 360px', minWidth: 0, background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: 8, padding: '8px 10px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', fontFamily: 'monospace' },
  pri: { display: 'inline-flex', alignItems: 'center', gap: 5, background: '#F4600C', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' },
  sec: { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--bg-input)', color: 'var(--text)', border: '1px solid var(--border2)', borderRadius: 8, padding: '8px 12px', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' },
  tag: { fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 99, letterSpacing: '.5px' },
  tagBlu: { color: '#1D4ED8', background: '#DBEAFE' },
  tagGrigio: { color: '#6E6E73', background: '#F2F2F5' },
  tagArancio: { color: '#C2410C', background: '#FFEDD5' },
  ok: { display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, fontSize: 'var(--font-size-sm)', color: '#15803D' },
  ko: { display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, fontSize: 'var(--font-size-sm)', color: '#B42318' },
};
